import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import hubHttpClient from 'hub-http/clients/apiClient';
import { useReactNodeView } from '../utils/ReactNodeView';
import { Popover } from 'foundations-components/transitional/components/Popover';
import MeetingsPopover from '../commands/meetings/MeetingsPopover';
import { MEETINGS } from 'hubspot-prosemirror/prosemirror/editorCapabilities';
import { fetchMeetings } from '../commands/meetings/fetchMeetings';
import Raven from 'raven-js';
import MeetingLink from '../commands/meetings/MeetingLink';
import { SENDER_MEETINGS_LINK_TOKEN, MEETINGS_LINK_TYPES as LINK_TYPES } from 'hubspot-prosemirror/draft-transplant/utils/constants';
import { buildMeetingTr } from '../utils/documentUtils';
import { jsx as _jsx } from "react/jsx-runtime";
export default function MeetingsView() {
  var _editorConfig$MEETING;
  const context = useReactNodeView();
  const [open, setOpen] = useState(false);
  const [meetingLinks, setMeetingLinks] = useState([]);
  const [hasFetchMeetingDone, setHasFetchMeetingDone] = useState(false);
  const initialAttrs = useMemo(() => context.node ? context.node.attrs : {}, [context.node]);
  const attrs = useRef(initialAttrs);
  const view = context.view;
  const editorConfig = context.editorConfig;
  const allowSenderMeetingLink = editorConfig === null || editorConfig === void 0 || (_editorConfig$MEETING = editorConfig[MEETINGS]) === null || _editorConfig$MEETING === void 0 ? void 0 : _editorConfig$MEETING.allowSenderMeetingLink;
  const {
    meetingURL,
    meetingName
  } = attrs.current;
  useEffect(() => {
    if (!hasFetchMeetingDone) {
      fetchMeetings({
        httpClient: hubHttpClient
      }).then(meetings => {
        setMeetingLinks(meetings);
      }).catch(error => {
        Raven.captureException(error);
      }).finally(() => {
        setHasFetchMeetingDone(true);
      });
    }
  }, [hasFetchMeetingDone]);
  const onOpenChange = useCallback(e => {
    if (e.target) {
      setOpen(e.target.value);
    }
  }, [setOpen]);
  const handleClick = useCallback(() => {
    if (!(view !== null && view !== void 0 && view.editable)) return;
    setOpen(prevState => !prevState);
  }, [setOpen, view]);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  const updateMeeting = useCallback(({
    meetingURL: updatedMeetingURL,
    meetingName: updatedMeetingName
  }) => {
    if (view) {
      const editorState = view.state;
      const tr = buildMeetingTr({
        meetingURL: updatedMeetingURL,
        meetingName: updatedMeetingName,
        editorState
      });
      // adding a delay to ensure the popover closes before the transaction is dispatched
      setTimeout(() => {
        view.dispatch(tr);
      }, 50);
    }
  }, [view]);
  const meetingPopover = useMemo(() => {
    const initialMeetingLinkType = meetingURL === SENDER_MEETINGS_LINK_TOKEN ? LINK_TYPES.SENDER : LINK_TYPES.CUSTOM;
    const initialMeetingName = meetingName === SENDER_MEETINGS_LINK_TOKEN ? '' : meetingName;
    return () => /*#__PURE__*/_jsx(MeetingsPopover, {
      initialMeetingName: initialMeetingName,
      initialMeetingLinkType: initialMeetingLinkType,
      initialMeetingURL: meetingURL,
      onChange: updateMeeting,
      onClose: handleClose,
      allowSenderMeetingLink: allowSenderMeetingLink,
      isNew: false
    });
  }, [allowSenderMeetingLink, handleClose, meetingName, meetingURL, updateMeeting]);
  return /*#__PURE__*/_jsx(Popover, {
    open: open,
    onOpenChange: onOpenChange,
    Content: meetingPopover,
    children: /*#__PURE__*/_jsx("span", {
      "data-test-id": "ProseMirror__meetings",
      children: /*#__PURE__*/_jsx(MeetingLink, {
        meetingName: meetingName,
        meetingURL: meetingURL,
        meetingLinks: meetingLinks,
        hasFetchMeetingDone: hasFetchMeetingDone,
        onClick: handleClick
      })
    })
  });
}
export const meetingsViewUpdateFn = ({
  previousNode,
  node
}) => {
  if (previousNode.attrs.meetingURL !== node.attrs.meetingURL || previousNode.attrs.meetingName !== node.attrs.meetingName) {
    return false;
  }
  return true;
};